import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

export type TimeBlock = { day: string; start: string; end: string };

const DAYS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
] as const;

const SLOT_MINUTES = 30;
// The whole day, not an office one. Home care starts before six and runs past
// ten: early personal care, evening medication, overnight sitting. A grid that
// cannot express a 05:30 visit silently tells a coordinator it is not allowed.
const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;
const SLOTS = ((DAY_END_HOUR - DAY_START_HOUR) * 60) / SLOT_MINUTES;

export function slotToTime(slot: number): string {
  const minutes = DAY_START_HOUR * 60 + slot * SLOT_MINUTES;
  // A block dragged to the bottom of the day ends at minute 1440, which this
  // would render as "24:00" — a string the plan's validator rejects, so the
  // save failed with "End time '24:00' is not HH:MM" and the coordinator could
  // not use the last half hour of the grid at all.
  //
  // It reads as the end of the day either way. Representing it properly would
  // mean carrying 24:00 through the validator, the TIME columns that staffing
  // is stored in, and the SQL that matches a visit's local time back to its
  // block — and Python's `time` cannot hold 24:00 at any point in that chain.
  // A block cannot cross midnight anyway, so the minute is not reachable by
  // any other means and nothing is lost by ending the day one minute early.
  if (minutes >= 24 * 60) return '23:59';
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function timeToSlot(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const offset = hours * 60 + minutes - DAY_START_HOUR * 60;
  // Rounded, not truncated, so the 23:59 that slotToTime emits for the end of
  // the day lands back on slot 48 rather than 47.97 — otherwise a block saved
  // against the last half hour would redraw a slot short, or not at all.
  return Math.round(offset / SLOT_MINUTES);
}

/**
 * A week of expected care, drawn rather than typed.
 *
 * What a block means matters more than how it looks: it is the *pattern of care
 * the plan authorises*, not a booking. Real visit times live on visits, and for
 * an agency with a system of record, there. A block here says personal care is
 * expected on Monday mornings; it puts nobody on a shift.
 *
 * Selection works by click-then-click as well as by dragging. A drag-only grid
 * cannot be used with a keyboard, by anyone whose hands are unsteady, or on a
 * touch screen where dragging scrolls the page — and this console is meant to
 * meet 44px targets and screen-reader use (launch gate 8).
 */
export function WeekGrid({
  blocks,
  onChange,
  inherited,
  readOnly = false,
}: {
  blocks: TimeBlock[];
  onChange?: (next: TimeBlock[]) => void;
  /** Shown faintly behind the editable blocks — the plan default a task would
   *  follow if it drew nothing of its own. */
  inherited?: TimeBlock[];
  readOnly?: boolean;
}) {
  const [anchor, setAnchor] = useState<{ day: string; slot: number } | null>(null);
  const [hover, setHover] = useState<{ day: string; slot: number } | null>(null);
  const dragging = useRef(false);

  const occupied = useMemo(() => {
    const map = new Map<string, TimeBlock>();
    for (const block of blocks) {
      const from = timeToSlot(block.start);
      const to = timeToSlot(block.end);
      for (let slot = from; slot < to; slot += 1) map.set(`${block.day}:${slot}`, block);
    }
    return map;
  }, [blocks]);

  const inheritedSlots = useMemo(() => {
    const set = new Set<string>();
    for (const block of inherited ?? []) {
      const from = timeToSlot(block.start);
      const to = timeToSlot(block.end);
      for (let slot = from; slot < to; slot += 1) set.add(`${block.day}:${slot}`);
    }
    return set;
  }, [inherited]);

  const pending = useMemo(() => {
    if (!anchor || !hover || anchor.day !== hover.day) return null;
    return { day: anchor.day, from: Math.min(anchor.slot, hover.slot), to: Math.max(anchor.slot, hover.slot) };
  }, [anchor, hover]);

  const commit = useCallback(
    (day: string, from: number, to: number) => {
      if (!onChange) return;
      const start = slotToTime(from);
      const end = slotToTime(to + 1);
      // Overlaps on the same day are merged rather than stacked: two
      // overlapping blocks describe one period of care, and keeping both would
      // mean the grid showed something the data did not.
      const sameDay = blocks.filter((b) => b.day === day);
      const others = blocks.filter((b) => b.day !== day);
      const merged = [...sameDay, { day, start, end }].sort((a, b) => a.start.localeCompare(b.start));
      const result: TimeBlock[] = [];
      for (const block of merged) {
        const last = result[result.length - 1];
        if (last && block.start <= last.end) {
          last.end = block.end > last.end ? block.end : last.end;
        } else {
          result.push({ ...block });
        }
      }
      onChange([...others, ...result]);
    },
    [blocks, onChange],
  );

  function cellDown(day: string, slot: number) {
    if (readOnly) return;
    const existing = occupied.get(`${day}:${slot}`);
    if (existing) {
      onChange?.(blocks.filter((b) => b !== existing));
      return;
    }
    if (anchor && anchor.day === day) {
      // Second click of a click-then-click selection.
      commit(day, Math.min(anchor.slot, slot), Math.max(anchor.slot, slot));
      setAnchor(null);
      setHover(null);
      return;
    }
    dragging.current = true;
    setAnchor({ day, slot });
    setHover({ day, slot });
  }

  function cellEnter(day: string, slot: number) {
    if (readOnly || !anchor) return;
    if (dragging.current) setHover({ day, slot });
  }

  function pointerUp() {
    if (!dragging.current || !anchor || !hover) {
      dragging.current = false;
      return;
    }
    dragging.current = false;
    // A click without movement leaves the anchor set, so the second click can
    // finish the range. Anything longer is a drag and commits now.
    if (anchor.slot !== hover.slot) {
      commit(anchor.day, Math.min(anchor.slot, hover.slot), Math.max(anchor.slot, hover.slot));
      setAnchor(null);
      setHover(null);
    }
  }

  return (
    <div className="weekgrid" onPointerUp={pointerUp} onPointerLeave={pointerUp}>
      <div className="weekgrid__corner" />
      {DAYS.map((day) => (
        <div key={day.value} className="weekgrid__dayhead">
          {day.label}
        </div>
      ))}

      {Array.from({ length: SLOTS }, (_, slot) => (
        <Fragment key={`row${slot}`}>
          <div className="weekgrid__time">
            {slot % 2 === 0 ? slotToTime(slot) : ''}
          </div>
          {DAYS.map((day) => {
            const key = `${day.value}:${slot}`;
            const block = occupied.get(key);
            const inPending =
              pending && pending.day === day.value && slot >= pending.from && slot <= pending.to;
            const state = block ? 'on' : inPending ? 'pending' : inheritedSlots.has(key) ? 'inherited' : 'off';
            return (
              <button
                key={key}
                type="button"
                className="weekgrid__cell"
                data-state={state}
                data-hour={slot % 2 === 0}
                data-major={slot % 12 === 0}
                disabled={readOnly}
                aria-label={`${day.label} ${slotToTime(slot)}${block ? ', scheduled. Select to remove' : ''}`}
                aria-pressed={Boolean(block)}
                onPointerDown={() => cellDown(day.value, slot)}
                onPointerEnter={() => cellEnter(day.value, slot)}
              />
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
